import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import * as Yup from "yup";

import { GridInputs } from "../../../../components/Functional";
import { Button, Modal, Spinner } from "../../../../components/ui";
import { HANDOVER_CONTACTS } from "../../../../graphql/mutation/Contact";
import { FETCH_CONTACTS_V2 as fetchContactsV2 } from "../../../../graphql/query/Contact";
import { useEntitiesLazy } from "../../../../hooks/useEntities";
import useUser from "../../../../hooks/useUser";

// Lightweight query: just the fields we need for the preview list. Asking for
// any more would balloon the request when the user has selected hundreds of
// contacts.
const HANDOVER_PREVIEW_QUERY = fetchContactsV2({
  name: true,
  salesUser: true,
});

const findEntityName = (entities, entityId) =>
  entities?.find(entity => entity?.id === entityId)?.name;

const HandoverPreview = ({
  contacts,
  loading,
  newSalesUserId,
  newSalesUserName,
}) => {
  if (loading) {
    return (
      <div className="flex h-32 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!contacts?.length) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        No contacts to hand over.
      </div>
    );
  }

  // Tailwind's preflight sets `border-collapse: collapse` on <table>, which
  // makes the bottom border of a sticky <thead> share an edge with the first
  // <tr> and visually disappear as rows scroll past. Switching the table to
  // `border-separate border-spacing-0` and putting `sticky top-0` on the
  // <th> cells themselves (with their own `border-b`) keeps the header
  // pinned with a clean underline in every browser, including older Safari.
  const headerCellClass =
    "sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-4 py-2 text-left font-medium text-gray-700";
  const bodyCellClass = "border-b border-gray-100 px-4 py-2";

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <div className="max-h-[50dvh] overflow-y-auto">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={headerCellClass}>Contact</th>
              <th className={headerCellClass}>Current Sales Contact</th>
              <th className={headerCellClass}>New Sales Contact</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map(contact => {
              const trimmedName = contact?.name?.trim();
              const hasName = Boolean(trimmedName);
              const currentName = contact?.salesUser?.name || "—";
              const nextName = newSalesUserName || "—";
              const isNoChange =
                newSalesUserId != null &&
                contact?.salesUser?.id === newSalesUserId;
              return (
                <tr key={contact?.id}>
                  <td
                    className={`${bodyCellClass} ${hasName ? "text-gray-900" : "font-medium text-red-600"}`}
                  >
                    {hasName ? trimmedName : "No Name"}
                  </td>
                  <td className={`${bodyCellClass} text-gray-600`}>
                    {currentName}
                  </td>
                  <td
                    className={`${bodyCellClass} ${isNoChange ? "text-gray-400" : "font-medium text-gray-900"}`}
                  >
                    {nextName}
                    {isNoChange ? " (no change)" : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const HandoverBody = (props = {}) => {
  const { closeModal, ids, isHandoverOpen = false } = props;
  const [handoverContacts, { loading }] = useMutation(HANDOVER_CONTACTS, {
    refetchQueries: ["FetchCRMRegistrars", "FetchContactsV2"],
  });
  const [fetchEntitiesLazily, { loading: entitiesLoading, entities }] =
    useEntitiesLazy();

  useEffect(() => {
    if (isHandoverOpen) {
      fetchEntitiesLazily();
      formik?.validateForm();
    }
  }, [isHandoverOpen]);

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({
      salesUserId: Yup.string().trim().required("Required"),
      description: Yup.string().trim(),
    }),
  });

  // Fetch the selected contacts so the user can see exactly which records will
  // be reassigned and from whom — surfacing any stray selections from earlier
  // pages or filters before the change is committed.
  const { data: previewData, loading: previewLoading } = useQuery(
    HANDOVER_PREVIEW_QUERY,
    {
      skip: !isHandoverOpen || !ids?.length,
      variables: {
        input: { id: { operator: "in", value: ids ?? [] }, limit: ids?.length },
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const previewContacts = previewData?.contactsV2?.edges ?? [];
  const newSalesUserId = formik?.values?.salesUserId
    ? +formik.values.salesUserId
    : null;
  const newSalesUserName = useMemo(
    () => (newSalesUserId ? findEntityName(entities?.edges, newSalesUserId) : null),
    [entities, newSalesUserId],
  );

  const inputProps = {
    formik,
    className: "grid grid-rows gap-4",
    inputs: [
      {
        name: "salesUserId",
        type: "multi-select",
        variant: "simple",
        label: "Primary Sales Contact",
        options: entities?.edges?.map(item => ({
          label: item?.name,
          value: item?.id,
        })),
      },
      {
        label: "Description",
        name: "description",
        type: "textarea",
        rows: 3,
      },
    ],
  };

  const handleClick = async () => {
    const { salesUserId, description } = formik?.values || {};
    const input = {
      ids,
      salesUserId,
    };

    if (description) {
      input.description = description;
    }

    const response = await handoverContacts({ variables: { input } });

    if (response?.data?.handoverContacts?.success) {
      closeModal();
      formik?.resetForm();
    }
  };

  // Cap the whole modal body to roughly the viewport so a handover preview
  // with hundreds of rows scrolls *inside* the table region instead of
  // pushing the modal taller and forcing a second outer scrollbar.
  return (
    <div className="flex max-h-[80dvh] w-full flex-col">
      <div className="flex flex-shrink-0 flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Handover Contacts</div>
          <div className="text-sm text-gray-500">
            {ids?.length ? (
              <>
                <span className="font-bold text-gray-900">
                  {ids.length} contact{ids.length === 1 ? "" : "s"}
                </span>{" "}
                will be reassigned to the new Primary Sales Contact.
              </>
            ) : (
              "No contacts selected."
            )}
          </div>
        </div>
        <div className="flex flex-col">
          <div>
            <Button
              action="default"
              className="mr-4"
              label="Cancel"
              onClick={() => {
                closeModal();
                formik?.resetForm();
              }}
            />
            <Button
              label={loading ? "Moving" : "Move"}
              disabled={loading || !formik?.values?.salesUserId}
              onClick={handleClick}
            />
          </div>
        </div>
      </div>
      {entitiesLoading ? (
        <div className="flex h-[30dvh] w-full items-center justify-center ">
          <Spinner />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-6 pt-4">
          <div className="grid flex-shrink-0 grid-cols-2 gap-6">
            <GridInputs {...inputProps} />
          </div>
          <HandoverPreview
            contacts={previewContacts}
            loading={previewLoading && !previewContacts.length}
            newSalesUserId={newSalesUserId}
            newSalesUserName={newSalesUserName}
          />
        </div>
      )}
    </div>
  );
};

const HandoverModal = (props = {}) => {
  const { ids } = props;
  const { hasPermission } = useUser();

  const disabled = !ids?.length || !hasPermission("HANDOVER_CONTACT");
  const modalProps = {
    body: HandoverBody,
    closeOnBackdrop: false,
    disabled,
    hideCloseButton: true,
    scale: "md",
    onClose: () => {
      // eslint-disable-next-line
      formik?.resetForm();
    },
    ...props,
  };

  return (
    <Modal {...modalProps}>
      <Button label="Handover" disabled={disabled} />
    </Modal>
  );
};

export { HandoverBody, HandoverModal };
